import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { GrowthModel } from "@/types/dssat";
import { Button, DateValue, RangeValue, ButtonGroup } from "@heroui/react";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
    Area,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { LAIQuantiles } from "@/types/water";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

const CHART_COLORS = {
    primary: "#09426A",
    secondary: "#74BD43",
    teal: "#00C4EB",
} as const;

const CHART_HEIGHT = 250;

function VegetativeTrendline({ dateRange }: { dateRange: RangeValue<DateValue> | null }) {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("insights");
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();

    const [startDate, endDate] = useMemo(() => {
        return [dayjs(dateRange?.start?.toString()), dayjs(dateRange?.end?.toString())];
    }, [dateRange]);

    const growthModelQuery = useGetRequest<GrowthModel[]>({
        url: `/fields/${fieldId}/growth_model/`,
        enabled: !!fieldId,
        params: {
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
    });

    const data = useMemo(() => {
        return sortArrayByKey(growthModelQuery.data ?? [], "capture_date").map(
            (row) => ({
                ...row,
                date: dayjs(row.capture_date).toDate(),
                ndre: [row.ndre_min, row.ndre_max],
                ndvi: [row.ndvi_min, row.ndvi_max],
            }),
        );
    }, [growthModelQuery.data]);

    const { irrigationEnabled } = useFeatureFlag();
    const laiQuery = useGetRequest<LAIQuantiles>({
        url: `/fields/${fieldId}/lai_quantiles/`,
        enabled: !!fieldId,
    });

    const laiData = useMemo(() => {
        return laiQuery.data?.data?.map((row) => ({
            ...row,
            date: dayjs(row.time).toDate(),
        }));
    }, [laiQuery.data]);

    const [key, setKey] = useState<"ndre" | "ndvi" | "lai">("ndre");

    const ndreNdviDomain = useMemo((): [number, number] | undefined => {
        if (!data.length || key === "lai") return undefined;
        const minKey = key === "ndre" ? "ndre_min" : "ndvi_min";
        const maxKey = key === "ndre" ? "ndre_max" : "ndvi_max";
        const dataMin = Math.min(...data.map((d) => d[minKey]));
        const dataMax = Math.max(...data.map((d) => d[maxKey]));
        const range = dataMax - dataMin || 0.1;
        const padding = range * 0.05;
        return [dataMin - padding, dataMax + padding];
    }, [data, key]);

    const chartTitle = key === "lai" ? "LAI" : key === "ndre" ? "NDRE" : "NDVI";

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between gap-2">
                <div className="font-semibold">{chartTitle}</div>
                <ButtonGroup size="sm" variant="flat">
                    <Button
                        color={key === "ndre" ? "primary" : "default"}
                        onPress={() => setKey("ndre")}
                        size="sm"
                    >
                        NDRE
                    </Button>
                    <Button
                        color={key === "ndvi" ? "primary" : "default"}
                        size="sm"
                        onPress={() => setKey("ndvi")}
                    >
                        NDVI
                    </Button>
                    {irrigationEnabled && (
                        <Button
                            color={key === "lai" ? "primary" : "default"}
                            size="sm"
                            onPress={() => setKey("lai")}
                        >
                            LAI
                        </Button>
                    )}
                </ButtonGroup>
            </div>
            {key === "lai" && (
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                    <LineChart
                        data={laiData}
                        margin={{ bottom: 8, left: 8, top: 15 }}
                    >
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            angle={-8}
                            fontSize={12}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                        />
                        <YAxis
                            label={{
                                value: "LAI",
                                angle: -90,
                                dx: -30,
                            }}
                            fontSize={12}
                            domain={["dataMin", "dataMax"]}
                            scale="linear"
                        />
                        <Tooltip
                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                            formatter={(value, name) => [
                                roundTo(value as number, 1),
                                name,
                            ]}
                            contentStyle={{ fontSize: 12 }}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                        <Legend
                            verticalAlign="top"
                            height={36}
                            fontSize={12}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                        <Line
                            dataKey="lai_masked_a"
                            stroke={CHART_COLORS.primary}
                            dot={false}
                            strokeWidth={2}
                            name={t("chartLabels.average", { ns: "insights" })}
                        />
                        <Line
                            dataKey="lai_masked_b"
                            stroke={CHART_COLORS.secondary}
                            dot={false}
                            strokeWidth={2}
                            name={t("chartLabels.best", { ns: "insights" })}
                        />
                        <Line
                            dataKey="lai_masked_w"
                            stroke={CHART_COLORS.teal}
                            dot={false}
                            strokeWidth={2}
                            name={t("chartLabels.worst", { ns: "insights" })}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
            {key !== "lai" && (
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                    <ComposedChart
                        data={data}
                        margin={{ bottom: 8, left: 8, top: 15 }}
                    >
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            angle={-8}
                            fontSize={12}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                        />
                        <YAxis
                            label={{
                                value: key === "ndre" ? "NDRE" : "NDVI",
                                angle: -90,
                                dx: -30,
                            }}
                            fontSize={12}
                            domain={ndreNdviDomain}
                            tickFormatter={(t) => `${roundTo(t, 4)}`}
                            tickCount={10}
                        />
                        <Tooltip
                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                            formatter={(value, _name, item) => {
                                const dataKey =
                                    (item as { dataKey?: string })?.dataKey;
                                if (dataKey === `${key}_mean`) {
                                    return [
                                        roundTo(value as number, 4),
                                        t("chartLabels.mean"),
                                    ];
                                }
                                if (dataKey === key) {
                                    const payload = item?.payload ?? {};
                                    const minVal = payload[`${key}_min`];
                                    const maxVal = payload[`${key}_max`];
                                    if (
                                        typeof minVal !== "number" ||
                                        typeof maxVal !== "number"
                                    ) {
                                        return [null, null];
                                    }
                                    return [
                                        `${roundTo(minVal, 4)} - ${roundTo(maxVal, 4)}`,
                                        t("chartLabels.minMax"),
                                    ];
                                }
                                return [null, null];
                            }}
                            contentStyle={{ fontSize: 12 }}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                        <Legend
                            verticalAlign="top"
                            height={36}
                            fontSize={12}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                        <Area
                            dataKey={key}
                            fill={CHART_COLORS.secondary}
                            fillOpacity={0.4}
                            strokeWidth={0}
                        />
                        <Line
                            dataKey={`${key}_mean`}
                            stroke={CHART_COLORS.primary}
                            dot={false}
                            strokeWidth={2}
                            name={t("chartLabels.mean")}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}

export default VegetativeTrendline;
